import { create } from "zustand"
import { combine } from "zustand/middleware"
import axios from "axios"

const fetcher = axios.create({
  baseURL: "http://localhost:8093",
})

interface MessageListener {
  id: string
  onMessage: (message: any) => void
}

const DEFAULT_STATE = {
  status: "UNKNOWN" as
    | "UNKNOWN"
    | "CONNECTED"
    | "CONNECTING"
    | "NOT_AVAILABLE"
    | "CLOSED",
  socket: null as WebSocket | null,
  cdsCode: "",
  scaleEnabled: false,
  listeners: [] as MessageListener[],
}

const openSocket = async (options?: {
  onOpen?: (socket: WebSocket) => void
}) => {
  return new Promise<WebSocket>((resolve, reject) => {
    const socket = new WebSocket("ws://localhost:8093/comm")

    socket.onopen = () => {
      socket.send(JSON.stringify({ action: "INIT" }))
      options?.onOpen?.(socket)
      resolve(socket)
    }

    socket.onerror = (error) => {
      socket.close()
      reject(new Error("Failed to connect to WebSocket"))
    }
  })
}

interface SyncPrinterPayload {
  action: string
  data: Array<{
    id: number
    name: string
    identifier: string
    label: string
    type: string
    kitchenPrinterGroupId: string
  }>
}

const useCompanionApp = create(
  combine(DEFAULT_STATE, (set, get) => ({
    async connect(options?: {
      syncKitchenPrinters?: (
        printers: Array<{ id: number; kitchenPrinterGroupId: string }>
      ) => void
      onDisconnect?: () => void
      onCDSMessage?: (data: {
        type:
          | "CHECKOUT"
          | "CART"
          | "BUSINESS"
          | "CUSTOMER"
          | "CASHIER"
          | "RECEIPT"
          | "POS_TYPE"
          | "PAYMENT_METHOD"
          | "PAYMENT_QR"
          | "CUSTOMER_VISIBILITY"
          | "CONNECTED"
          | "CUSTOMER_DISPLAY_ADS"
        payload: any
      }) => void
      onOpen?: (socket: WebSocket) => void
    }) {
      try {
        if (get().status === "CONNECTED") {
          return get().socket
        }
        const socket = await openSocket(options)
        set({ socket, status: "CONNECTED" })

        socket.onmessage = (event) => {
          let message: any = null
          try {
            message = JSON.parse(event.data)
          } catch (e) {}
          if (message === null) return

          const listeners = get().listeners

          // Notify all registered listeners with the full message
          listeners.forEach((listener) => {
            listener.onMessage(message)
          })

          // Keep existing implementation for direct callbacks
          if (message.action === "SYNC_KITCHEN_PRINTERS") {
            const payload = message as SyncPrinterPayload
            if (options?.syncKitchenPrinters) {
              options.syncKitchenPrinters(payload.data)
            }
          } else if (message.action === "CDS_MESSAGE") {
            if (options?.onCDSMessage) {
              options.onCDSMessage(message.data)
            }
          } else if (message.action === "SCALE_STATUS") {
            // Handle scale status updates
            const enabled = message.data?.scaleEnabled ?? false
            set({ scaleEnabled: enabled })
          }
        }

        socket.onclose = () => {
          set({ status: "CLOSED" })
          if (options?.onDisconnect) {
            options.onDisconnect()
          }
        }

        return socket
      } catch (error) {
        console.error(error)
        if (get().status === "UNKNOWN") {
          set({ status: "NOT_AVAILABLE" })
        }
        return null
      }
    },
    async printThermal(
      type: "RECEIPT" | "KITCHEN" | "LABEL",
      configId: string | null,
      data: Uint8Array
    ): Promise<void> {
      let socket = get().socket

      if (!socket || socket.readyState !== WebSocket.OPEN) {
        socket = await this.connect()
        if (!socket) {
          throw new Error("Companion app not available")
        }
      }

      const message = JSON.stringify({
        action: "PRINT",
        type: type,
        configId: configId,
        data: Array.from(data),
      })

      socket.send(message)
    },
    ping() {
      get().socket?.send("PING")
    },
    // sends message to any CDS connected to the companion app
    sendCDSMessage(data: {
      type:
        | "CHECKOUT"
        | "CART"
        | "BUSINESS"
        | "CUSTOMER"
        | "CASHIER"
        | "RECEIPT"
        | "POS_TYPE"
        | "PAYMENT_METHOD"
        | "PAYMENT_QR"
        | "CUSTOMER_VISIBILITY"
        | "CUSTOMER_DISPLAY_ADS"
      payload: any
    }) {
      // Trim data to only include fields needed by CDS
      let processedData = data
      if (data.type === "CART" && data.payload) {
        processedData = {
          ...data,
          payload: trimCartForCDS(data.payload),
        }
      } else if (data.type === "BUSINESS" && data.payload) {
        processedData = {
          ...data,
          payload: trimBusinessForCDS(data.payload),
        }
      } else if (data.type === "CHECKOUT" && data.payload) {
        processedData = {
          ...data,
          payload: trimCheckoutForCDS(data.payload),
        }
      }

      get().socket?.send(
        JSON.stringify({
          action: "SEND_CDS_MESSAGE",
          data: processedData,
        })
      )
    },
    sendScaleOperation(operation: "READ_WEIGHT" | "TARE" | "ZERO") {
      get().socket?.send(
        JSON.stringify({
          action: "SCALE",
          operation: operation,
        })
      )
    },
    addMessageListener(listener: MessageListener) {
      set((state) => ({
        listeners: [...state.listeners, listener],
      }))
    },
    removeMessageListener(listenerId: string) {
      set((state) => ({
        listeners: state.listeners.filter((l) => l.id !== listenerId),
      }))
    },
  }))
)

// Helper function to trim cart data for CDS
export const trimCartForCDS = (cart: any): any => {
  if (!cart || !cart.items) {
    return cart
  }

  return {
    items: cart.items.map((item: any) => ({
      id: item.id,
      qty: item.qty,
      sellingPrice: item.sellingPrice,
      productVariant: item.productVariant,
      product: item.product
        ? {
            name: item.product.name,
            productVariants: item.product.productVariants?.map(
              (variant: any) => ({
                id: variant.id,
                variantValues: variant.variantValues?.map((value: any) => ({
                  productOptionValue: {
                    name: value.productOptionValue?.name,
                  },
                })),
              })
            ),
          }
        : undefined,
      orderedProductAddOns: item.orderedProductAddOns?.map((addon: any) => ({
        productAddOns: {
          id: addon.productAddOns?.id,
          name: addon.productAddOns?.name,
          sellingPrice: addon.productAddOns?.sellingPrice,
        },
      })),
    })),
    promotion: cart.promotion,
    manualDiscount: cart.manualDiscount,
  }
}

// Helper function to trim business data for CDS
export const trimBusinessForCDS = (business: any): any => {
  if (!business) {
    return business
  }

  return {
    id: business.id,
    logoUrl: business.logoUrl,
    name: business.name,
    businessCurrency: business.businessCurrency,
    country: business.country
      ? {
          currency: business.country.currency,
        }
      : undefined,
  }
}

// Helper function to trim checkout data for CDS
export const trimCheckoutForCDS = (checkout: any): any => {
  if (!checkout) {
    return checkout
  }

  return {
    taxes: checkout.taxes,
    totalAmountWithoutDiscount: checkout.totalAmountWithoutDiscount,
    totalAmountWithDiscount: checkout.totalAmountWithDiscount,
    taxAmount: checkout.taxAmount,
    serviceChargeAmount: checkout.serviceChargeAmount,
    totalDiscount: checkout.totalDiscount,
    taxExemptions: checkout.taxExemptions?.map(
      (exemption: { amount: number }) => ({
        amount: exemption.amount,
      })
    ),
  }
}

export default useCompanionApp
